import { Fragment } from "prosemirror-model";
import { getSchema } from "@tiptap/core";
import { FULL_DOCUMENT_EXTENSIONS } from "../shared/schemas/tiptapSchemaProfiles";
import { stripUnknownMarks } from "../shared/schemas/stripUnknownMarks.js";

export { stripUnknownMarks };

export const extensions = FULL_DOCUMENT_EXTENSIONS;

export default function getProseMirrorSchema() {
  return getSchema(extensions);
}

// Only schema-owned names may appear in diagnostics. Unknown type strings are
// untrusted document data too.
export function getDiagnosticNodeType(node, schema) {
  if (!node?.type) return "<undefined>";
  return typeof node.type === "string" && Object.hasOwn(schema.nodes, node.type)
    ? node.type : "<unknown>";
}

export function getDocumentSchemaError(json, schema) {
  const first = getDocumentSchemaErrors(json, schema)[0];
  return first ? `${first.path}: ${first.error}` : null;
}

/**
 * Validate bottom-up so a descendant's failure is reported at its own path,
 * rather than repeated on every ancestor. Never forward exception messages:
 * ProseMirror includes manuscript text in them, sometimes escaped or truncated.
 */
export function getDocumentSchemaErrors(json, schema) {
  const errors = [];
  const walk = (jsonNode, path) => {
    const nodeType = getDiagnosticNodeType(jsonNode, schema);
    const type = schema.nodes[nodeType];
    const rule = type?.spec.content || null;
    const fail = (error, expectedRule = rule) => {
      errors.push({ path, nodeType, error, rule: expectedRule });
      return null;
    };
    if (path === "root" && jsonNode?.type !== schema.topNodeType.name) {
      return fail(`Document root must be "${schema.topNodeType.name}"`, schema.topNodeType.name);
    }
    if (!type) return fail("Missing or unknown node type");
    if (jsonNode.content != null && !Array.isArray(jsonNode.content)) {
      return fail("Node content must be an array");
    }
    const children = (jsonNode.content || []).map((child, i) => walk(child, `${path}.content[${i}]`));
    if (children.some(child => !child)) return null;
    try {
      // Decode only this node; its children have already been constructed.
      let node = schema.nodeFromJSON({ ...jsonNode, content: undefined });
      if (node.isText && children.length) return fail("Text nodes cannot have children");
      if (!node.isText) node = node.copy(Fragment.fromArray(children));
      if (!type.validContent(node.content)) {
        return fail(`Invalid content for node ${nodeType}; expected ${rule || "no children"}`);
      }
      node.check();
      return node;
    } catch {
      return fail(`Invalid node encoding, attributes, or marks for ${nodeType}`);
    }
  };
  walk(json, "root");
  return errors;
}

/**
 * Recursively replaces nodes whose type isn't in the schema with paragraphs,
 * preserving any text content found within them. Nodes that are both invalid
 * and contain no text are removed. The root node (type "doc") is never touched.
 *
 * Valid node types are derived from the schema, so this stays in sync with
 * whatever extensions are registered.
 *
 * @param {{type: string, content?: object[], text?: string, marks?: object[], attrs?: object}} json
 * @param {import("prosemirror-model").Schema} schema
 * @param {{ onContentLoss?: (loss: { path: string, nodeType: string, validDescendants: string[] }) => void, repairEmptyListItems?: boolean }} [options]
 */
export function stripInvalidNodes(json, schema, { onContentLoss, repairEmptyListItems = false } = {}) {
  if (!json) return json;

  const validNodes = new Set(Object.keys(schema.nodes));

  // Extract all text from a node tree recursively
  const collectText = (node) => {
    if (!node) return "";
    if (node.type === "text") return node.text || "";
    let text = "";
    if (node.content && Array.isArray(node.content)) {
      for (const child of node.content) {
        text += collectText(child);
      }
    }
    return text;
  };

  // Check if a node tree contains valid descendants whose content cannot be
  // rescued as text (e.g. images). Text-container nodes like paragraphs have
  // their text preserved by collectText, so they are not counted as lost.
  const hasUnrescuableContent = (node) => {
    if (!node) return false;
    if (node.type && node.type !== "text" && validNodes.has(node.type)) {
      // This is a valid non-text node. Is its content rescuable as text?
      if (collectText(node) === "") return true; // No text -> truly lost (e.g. image)
      // Has text -> text will be rescued, but check deeper for unrescuable siblings
    }
    if (node.content && Array.isArray(node.content)) {
      return node.content.some(hasUnrescuableContent);
    }
    return false;
  };

  // Collect valid non-text descendant types whose content cannot be rescued
  // as text — only types where collectText returns "" (e.g. image, horizontalRule)
  const collectUnrescuableTypes = (node) => {
    const types = [];
    const walk = (n) => {
      if (!n) return;
      if (n.type && n.type !== "text" && validNodes.has(n.type) && collectText(n) === "") {
        types.push(n.type);
      }
      if (n.content && Array.isArray(n.content)) {
        n.content.forEach(walk);
      }
    };
    if (node.content && Array.isArray(node.content)) {
      node.content.forEach(walk);
    }
    return types;
  };

  const sanitize = (node, path) => {
    if (!node) return null;

    // ProseMirror's Node.fromJSON throws on text nodes with empty/missing text.
    if (node.type === "text" && !node.text) return null;

    // Valid node — recurse into children
    if (node.type && validNodes.has(node.type)) {
      const result = { ...node };
      if (result.content && Array.isArray(result.content)) {
        result.content = result.content.map((child, i) => sanitize(child, `${path}.content[${i}]`)).filter(Boolean);
      }
      // Repair empty list items: listItem and taskItem must contain at least one
      // block child. This handles both originally-empty items and ones whose
      // children were all unknown nodes that got stripped above.
      if (repairEmptyListItems && (result.type === "listItem" || result.type === "taskItem") && (result.content == null || (Array.isArray(result.content) && result.content.length === 0))) {
        result.content = [{ type: "paragraph" }];
      }
      return result;
    }

    // Before attempting text rescue, check for valid non-text descendants
    // that would be silently lost (e.g., images inside an unknown wrapper
    // that also contains text — the text is rescued but images are dropped).
    if (onContentLoss && hasUnrescuableContent(node)) {
      onContentLoss({
        path,
        nodeType: node.type || "<undefined>",
        validDescendants: collectUnrescuableTypes(node),
      });
    }

    // Invalid node — try to preserve text as a paragraph
    const text = collectText(node);
    if (text) {
      return { type: "paragraph", content: [{ type: "text", text }] };
    }

    // Invalid and empty — drop it
    return null;
  };

  // Never strip the root doc node itself
  const result = { ...json };
  if (result.content && Array.isArray(result.content)) {
    result.content = result.content.map((child, i) => sanitize(child, `root.content[${i}]`)).filter(Boolean);
  }
  return result;
}
